import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { BaseModal } from "../components/modals/BaseModal";
import { useEffect, useState } from "react";
import { handleApi } from "../utils/handleApi";
import { createCreditPurchaseLink } from "../services/payment/Credits";
import toast from "react-hot-toast";
import { membershipStatsAxios } from "../services/membership/MembershipAxios";
import { format } from "date-fns";
import { convertToZone } from "../utils/formatters";
import { useAuth } from "../context/AuthContext";
import { dictionary } from "../utils/dictionary";

interface PaymentScenario {
  imageSrc: string;
  titleKey: string;
  descriptionKey: string;
  buttonOne: {
    textKey: string;
    redirectTo: string;
  };
  buttonTwo?: {
    textKey: string;
    redirectTo: string;
  };
}

const paymentConfig: Record<"success" | "cancel", Record<"subscription" | "credits", PaymentScenario>> = {
  success: {
    subscription: {
      imageSrc: "/gif/Success.gif",
      titleKey: "payment-successful",
      descriptionKey: "subscription-success-description",
      buttonOne: {
        textKey: "continue",
        redirectTo: "/home"
      }
    },
    credits: {
      imageSrc: "https://fluxi.s3.amazonaws.com/prod/assets/media/green-card.webm",
      titleKey: "credits-purchased-title",
      descriptionKey: "credits-success-description",
      buttonOne: {
        textKey: "continue",
        redirectTo: "/membership"
      }
    }
  },
  cancel: {
    subscription: {
      imageSrc: "/gif/delete_page.gif",
      titleKey: "payment-failed",
      descriptionKey: "payment-failed-description",
      buttonOne: {
        textKey: "try-again",
        redirectTo: "/plans"
      },
      buttonTwo: {
        textKey: "cancel",
        redirectTo: "/home"
      }
    },
    credits: {
      imageSrc: "https://fluxi.s3.amazonaws.com/prod/assets/media/red-card.webm",
      titleKey: "purchase-cancelled",
      descriptionKey: "credits-cancel-description",
      buttonOne: {
        textKey: "try-again",
        redirectTo: "/membership"
      },
      buttonTwo: {
        textKey: "cancel",
        redirectTo: "/home"
      }
    }
  }
};

export const PaymentResultPage = () => {
  const { status } = useParams<{ status: "success" | "cancel" }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useAuth();
  const [isOpen, setIsOpen] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [endMembershipDateFormatted, setEndMembershipDateFormatted] = useState("");
  const dateFormat = "dd/MM/yyyy";

  const getStats = async () => {
    try {
      const stats = await membershipStatsAxios();

      const formattedDate = convertToZone(
        format(new Date(stats.active_membership.ends_at || ""), dateFormat),
        dateFormat
      )

      setEndMembershipDateFormatted(formattedDate);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    getStats();
  }, []);
  
  const creditsAmount = searchParams.get("credits");
  const type: "subscription" | "credits" = creditsAmount ? "credits" : "subscription";
  
  const planName = searchParams.get("plan") || "Premium";
  const validStatus = (status || "success") as "success" | "cancel";
  
  const config = paymentConfig[validStatus]?.[type] || paymentConfig.success.subscription;
  
  // Obtener textos traducidos
  const title = dictionary[config.titleKey]?.[language] || config.titleKey;
  const buttonOneText = dictionary[config.buttonOne.textKey]?.[language] || config.buttonOne.textKey;
  const buttonTwoText = config.buttonTwo 
    ? dictionary[config.buttonTwo.textKey]?.[language] || config.buttonTwo.textKey 
    : undefined;
  
  let description = dictionary[config.descriptionKey]?.[language] || config.descriptionKey;
  if (type === "subscription") {
    description = description.replace("[plan_name]", planName);
  } else {
    description = description.replace(/\[credits_amount\]/g, creditsAmount || "0");
    description = description.replace("[expiration_date]", endMembershipDateFormatted || "...");
  }

  useEffect(() => {
    if (!status || !["success", "cancel"].includes(status)) {
      navigate("/home");
    }
  }, [status, navigate]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      navigate(config.buttonOne.redirectTo);
    }, 200);
  };

  const handleRetryPurchase = async () => {
    if (!creditsAmount) return;
    
    setIsRetrying(true);
    const { res } = await handleApi(createCreditPurchaseLink(parseInt(creditsAmount)));
    
    if (res && res.payment_url) {
      window.location.href = res.payment_url;
    } else {
      setIsRetrying(false);
      toast.error(dictionary["error-creating-purchase-link"]?.[language] || "Error creating purchase link");
    }
  };

  const handleButtonOne = () => {
    if (validStatus === "cancel") {
      if (type === "credits") {
        handleRetryPurchase();
      } else {
        navigate(config.buttonOne.redirectTo);
      }
    } else {
      navigate(config.buttonOne.redirectTo);
    }
  };

  const handleButtonTwo = () => {
    if (config.buttonTwo) {
      navigate(config.buttonTwo.redirectTo);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={description}
      imageSrc={config.imageSrc}
      imageAlt={status === "success" 
        ? dictionary["payment-successful"]?.[language] || "Payment successful" 
        : dictionary["payment-failed"]?.[language] || "Payment failed"}
      buttonOne={{
        text: buttonOneText,
        onClick: handleButtonOne,
        loading: isRetrying,
        disabled: isRetrying
      }}
      buttonTwo={config.buttonTwo ? {
        text: buttonTwoText || "",
        onClick: handleButtonTwo,
        disabled: isRetrying
      } : undefined}
      showCloseButton={status === "success"}
    />
  );
};

